import { Comment, useApi } from "@soft-db/monitoring-api-client";
import { ref, watch } from "vue";
import { useCurrentUser } from "../../api/current-user";
import { CommentBar } from "../../components/dashboard/plugins/comment-bar";
import { CommentBox } from "../../components/dashboard/plugins/comment-box";
import { createRestrictiveInjectionState } from "../../composable/restrictive-injection-state";
import { useDashboardLayout } from "./dashboard-layout";
import { useDashboardState } from "./dashboard-state";
import { useTimeRangeManager } from "./dashboard-time";

const initDashboardComments = () => {
	const archivistApi = useApi().archivist();

	const { context } = useDashboardState();
	const { range } = useTimeRangeManager();
	const layouts = useDashboardLayout();

	const comments = ref<Comment[]>([]);

	async function fetch() {
		if (!context.value) return;

		const { site } = context.value;

		const fetchedComments = await archivistApi.fetchComments(site, {
			startTime: range.value.start.toJSDate(),
			endTime: range.value.end.toJSDate(),
		});
		if (fetchedComments) {
			const unJsonifyComments: Comment[] = fetchedComments.map((comment) => ({
				id: comment.id,
				startTime: new Date(comment.startTime),
				endTime: new Date(comment.endTime),
				content: comment.content,
				creator: comment.creator,
			}));
			comments.value = unJsonifyComments;
		}
	}

	const hasComments = computed(() => {
		return layouts.currentLayout.value?.panels.some(
			(p) => p.plugin.type === CommentBar.id || p.plugin.type === CommentBox.id
		);
	});

	watch(range, () => {
		if (hasComments.value) {
			void fetch();
		}
	});

	function reset() {
		comments.value = [];
	}

	const { isPublic } = useCurrentUser();
	const allowCreation = computed(() => {
		return !isPublic.value;
	});

	return reactive({
		comments,
		fetch,
		reset,
		allowCreation,
	});
};

export const [useDashboardCommentsProvider, useDashboardComments] = createRestrictiveInjectionState(
	initDashboardComments,
	"Dashboard must be provided in parent component"
);
